import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useComplaintModal } from "../context/ComplaintModalContext";
import { listarEmpresas, listarVagas, iaChat, obterMeuPerfil, salvarPerfil } from "../services/api";

/**
 * Mascote "Eaí" — assistente flutuante do eaiRH.
 * Arquitetura HÍBRIDA: cérebro LOCAL de regras (grátis, instantâneo) como 1ª camada; quando não há match
 * (ou em pergunta sobre empresa específica) e o usuário está LOGADO, chama a IA real (Claude) via iaChat().
 * Deslogado fica só nas regras + resumo-template (IA é exclusiva de quem se logou — decisão de custo).
 */
// PAGINAS DE TAREFA UNICA NAO TEM CHAT.
//
// Em 27/08/2026 uma pessoa abriu o link de feedback, ficou 0 segundo nele e foi parar na tela de login.
// A causa foi o menu do site competindo com o formulario; o balao do chat e o mesmo tipo de distracao,
// oferecendo uma conversa a quem acabou de receber um "nao" e tem tres perguntas pela frente.
//
// Vale para /feedback e /autorizar: as duas existem para a pessoa fazer UMA coisa e fechar a aba.
const ROTAS_SEM_CHAT = ["/feedback/", "/autorizar/"];

/**
 * 🔐 "JÁ PERGUNTEI NESTA SESSÃO?" — marcador em sessionStorage, por usuário. (26/09/2026)
 *
 * Regra que o Nelson definiu: *"precisa ser por login e por acesso, cada pessoa precisa ver somente o
 * seu, e se for logado limpar e perguntar se quer retomar a conversa da sessão anterior ou iniciar uma
 * nova"*.
 *
 * sessionStorage (e não localStorage) porque ele morre quando a aba fecha: assim a pergunta volta a
 * cada ACESSO novo. E a chave leva o id do usuário, então trocar de conta no mesmo navegador também
 * pergunta de novo — que é a outra metade do pedido.
 *
 * F5 não pergunta: recarregar página não é acesso novo, e perguntar ali só atrapalharia quem está no
 * meio de uma conversa.
 */
function jaDecidiuNestaSessao(uid) {
  try { return sessionStorage.getItem(`eairh_chat_ask_${uid}`) === "1"; } catch { return true; }
}
function marcarDecidido(uid) {
  try { sessionStorage.setItem(`eairh_chat_ask_${uid}`, "1"); } catch { /* privado */ }
}
function temConversaGuardada(chave) {
  try {
    const c = JSON.parse(localStorage.getItem(chave) || "[]");
    return Array.isArray(c) && c.some((m) => m.text);
  } catch { return false; }
}

/** Lê a conversa de UMA chave. A saudação (`bemvindo`) nunca volta do storage: ela é remontada do código
 *  a cada abertura, senão quem já usou o chat fica preso no menu antigo para sempre. */
function lerConversa(chave) {
  try {
    const s = localStorage.getItem(chave);
    return s ? (JSON.parse(s) || []).filter((m) => !m.bemvindo) : [];
  } catch { return []; }
}

export default function ChatWidget() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { openComplaint } = useComplaintModal();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [balao, setBalao] = useState(true);
  // Persistência: a conversa fica no localStorage por usuário, então NÃO some ao atualizar a página.
  //
  // ⚠️ A SAUDAÇÃO NÃO É HISTÓRICO (24/09/2026). Ela era salva junto com os chips, e como a saudação só
  // é montada quando a conversa está vazia, quem já tinha aberto o chat UMA vez ficava preso para sempre
  // no menu antigo — inclusive o chip novo de buscar vagas, que nunca chegou a ninguém que já usou o
  // chat. Ctrl+F5 não resolve (limpa cache HTTP, não o localStorage) e foi exatamente o que aconteceu
  // com o Nelson: bundle novo no ar, tela velha na frente dele.
  //
  // Duas travas: a chave ganhou `_v2` (descarta de uma vez o que já está gravado nos navegadores) e a
  // saudação vai marcada com `bemvindo`, para ser descartada na leitura e remontada do código a cada
  // abertura. Assim a próxima mudança de menu chega a todo mundo sem depender de limpar nada.
  //
  // 🚨 VAZAMENTO NO LOGOUT — corrigido em 26/09/2026, achado pelo Nelson: *"fiz logoff e ele manteve a
  // conversa de quando estava logado"*.
  //
  // O `useState(() => ...)` lê o localStorage UMA vez, na montagem. No logout o componente não remonta:
  // as mensagens da pessoa continuavam em memória e a `storeKey` virava `_anon` — então o efeito que
  // salva **gravava a conversa do usuário logado dentro da chave do visitante anônimo**. Em navegador
  // compartilhado (casa, lan house, computador do balcão), o próximo a abrir o chat sem login lia a
  // conversa de quem esteve ali antes.
  //
  // A correção tem duas partes, e as duas importam:
  //   1. recarregar a conversa SEMPRE que o dono muda (o useEffect abaixo);
  //   2. `carregadaDe` — só salvar depois de a carga da chave atual ter acontecido. Sem isso existe um
  //      render entre "a chave mudou" e "a conversa foi recarregada", e é exatamente nele que o conteúdo
  //      antigo era gravado na chave nova.
  const storeKey = `eairh_chat_v2_${user?.id || "anon"}`;
  const carregadaDe = useRef(storeKey);

  // Pergunta pendente: existe conversa guardada, a pessoa está logada e ainda não escolheu nesta sessão.
  // Enquanto `perguntar` for true a tela fica LIMPA — é o "limpar e perguntar" do pedido.
  const [perguntar, setPerguntar] = useState(
    () => !!user?.id && !jaDecidiuNestaSessao(user.id) && temConversaGuardada(`eairh_chat_v2_${user.id}`)
  );
  const [msgs, setMsgs] = useState(() => {
    const k = `eairh_chat_v2_${user?.id || "anon"}`;
    if (user?.id && !jaDecidiuNestaSessao(user.id) && temConversaGuardada(k)) return [];
    return lerConversa(k);
  });

  const [texto, setTexto] = useState("");
  // 🔎 BUSCA DE VAGAS GUIADA (20/09/2026, pedido do Nelson: "o assistente tem como colocar a opção nele de
  // buscar vagas para mim? e ele já filtrar vagas relevantes para o candidato? sem usar IA para não gastar?
  // ele pode por exemplo perguntar quais filtros o candidato quer usar").
  //
  // SEM IA de propósito, e não é só economia: num portal de vagas, o pior defeito possível é o assistente
  // INVENTAR uma vaga que não existe. Aqui a resposta sai do mesmo /api/vagas que desenha o mural — se
  // aparece no chat, existe e abre. Custo zero, resposta instantânea, zero alucinação.
  //
  // O fluxo é de duas perguntas (o que + onde) porque é o que o filtro do mural realmente usa. Uma terceira
  // pergunta a mais e a pessoa desiste antes de ver vaga nenhuma.
  // 👤 O ASSISTENTE PRECISA SABER QUEM ESTÁ FALANDO COM ELE (26/09/2026).
  //
  // O Nelson, logado e com "Tecnologia da Informação" no currículo, foi buscar vaga e o chat perguntou
  // "o que você faz?" como se não o conhecesse: *"estou logado e ele não reconheceu minha área de
  // atuação"*. Pedir de novo o que a pessoa já preencheu é o tipo de atrito que faz desistir.
  //
  // Carregado UMA vez, ao abrir o chat, e só para candidato logado. Falha em silêncio: sem perfil, o
  // fluxo volta a ser o de antes — perguntar é pior que não perguntar, mas melhor que quebrar.
  const [meuPerfil, setMeuPerfil] = useState(null);
  useEffect(() => {
    if (!aberto || !user || user.tipo !== 0 || meuPerfil) return;
    obterMeuPerfil().then(setMeuPerfil).catch(() => {});
  }, [aberto, user, meuPerfil]);

  const [busca, setBusca] = useState(null);   // null | { passo: "oque"|"onde", oque?: string }
  const fimRef = useRef(null);

  // Trocou de dono (login, logout ou troca de conta): a conversa da tela passa a ser a DAQUELE dono.
  useEffect(() => {
    if (carregadaDe.current === storeKey) return;
    carregadaDe.current = storeKey;
    setBusca(null);      // a busca guiada carrega cidade/área de quem estava usando — não sobrevive à troca
    setTexto("");

    // Logou agora: se há conversa guardada e ele ainda não escolheu nesta sessão, a tela começa LIMPA
    // e com a pergunta. Sem conversa guardada (ou já decidido), segue o fluxo normal.
    const pendente = !!user?.id && !jaDecidiuNestaSessao(user.id) && temConversaGuardada(storeKey);
    setPerguntar(pendente);
    setMsgs(pendente ? [] : lerConversa(storeKey));
  }, [storeKey]);


  // Salva as últimas 30 mensagens (sem placeholders) sempre que a conversa muda.
  // ⚠️ `carregadaDe.current !== storeKey` = o dono acabou de mudar e a conversa nova ainda não foi lida.
  // Gravar agora colocaria o histórico do dono anterior na chave do novo. Ver o comentário lá em cima.
  useEffect(() => {
    if (carregadaDe.current !== storeKey) return;
    // ⚠️ Com a pergunta aberta a tela está vazia DE PROPÓSITO. Salvar aqui gravaria esse vazio por cima
    // da conversa anterior — e a opção "retomar" não teria mais o que retomar.
    if (perguntar) return;
    try { localStorage.setItem(storeKey, JSON.stringify(msgs.filter((m) => m.text && !m.loading).slice(-30))); } catch { /* quota/privado */ }
  }, [msgs, storeKey, perguntar]);

  const primeiroNome = user?.nome ? user.nome.split(" ")[0] : null;
  const saudacao = primeiroNome ? `Oi, ${primeiroNome}!` : "Oi, tudo bem?";
  // Logado → tom de ajuda; visitante (deslogado) → boas-vindas + convite a conhecer a plataforma.
  const textoBalao = user
    ? `${saudacao} Precisa de ajuda? Posso explicar reputação, relatos e currículo.`
    : "Oi! 👋 Sou o Eaí. Quer conhecer o eaiRH? Te mostro a reputação das empresas nos processos seletivos — e é grátis criar sua conta.";

  useEffect(() => {
    // A saudação atual entra SEMPRE no topo ao abrir — com conversa antiga embaixo ou sem nenhuma.
    if (aberto) {
      if (perguntar) return;   // a escolha vem primeiro; a saudação entra depois que ela for feita
      setMsgs((m) => (m.some((x) => x.bemvindo) ? m : [{ from: "bot", ...boasVindas(user) }, ...m]));
    }
    // 🚨 `perguntar` PRECISA ESTAR AQUI (26/09/2026). Sem ele, clicar em "Começar uma nova" virava
    // `perguntar = false` e o efeito NÃO rodava de novo — a saudação nunca era montada e a janela ficava
    // em branco. O Nelson teve que fechar e abrir o chat para ver alguma coisa: *"o certo seria reiniciar
    // ele ao clicar em nova conversa"*. É o que acontece agora: a conversa nova começa como se o chat
    // tivesse acabado de abrir, com saudação e menu.
  }, [aberto, perguntar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, aberto]);

  // Memória: últimas trocas reais (sem placeholders/chips) para a IA ter contexto ("ela", "essa empresa"...).
  const fazerHistorico = () =>
    msgs.filter((m) => m.text && !m.loading).slice(-6).map((m) => ({ autor: m.from === "user" ? "user" : "bot", texto: m.text }));

  // Um passo do fluxo: guarda o que a pessoa respondeu e ou pergunta o resto, ou já busca.
  // 🔢 CONTA SEM MOSTRAR. A API devolve `total` com take=1, então cada passo do funil sabe quantas vagas
  // sobraram antes de perguntar a próxima coisa — que é o que deixa a pessoa decidir se refina ou já vê.
  const contar = async (filtros) => {
    try { const d = await listarVagas({ ...filtros, take: 1 }); return d?.total ?? 0; }
    catch { return null; }
  };

  const buscarEMostrar = async (filtros, resumo) => {
    setMsgs((m) => [...m, { from: "bot", text: "🔎 Procurando no mural...", loading: true }]);
    try {
      const d = await listarVagas({ ...filtros, take: 5 });
      // 🕳️ FUNIL VAZIO TEM SAÍDA. Antes o "não achei" era um beco: a pessoa via zero e ia embora. Agora,
      // quando não há resultado, a gente CONTA quanto teria sem o último filtro e oferece o caminho —
      // é aritmética, não IA, e é a diferença entre "não tem vaga" e "não tem COM esse filtro".
      if ((d?.total ?? 0) === 0) {
        const semMod = { ...filtros }; delete semMod.modalidade; delete semMod.remoto;
        const semLocal = { ...semMod }; delete semLocal.cidade;
        const [a, b] = [await contar(semMod), await contar(semLocal)];
        setMsgs((m) => [...m.filter((x) => !x.loading), { from: "bot", ...semResultado(filtros, resumo, a, b) }]);
        return;
      }
      setMsgs((m) => [...m.filter((x) => !x.loading), { from: "bot", ...resultadoVagas(d, resumo, filtros) }]);
    } catch {
      setMsgs((m) => [...m.filter((x) => !x.loading), { from: "bot", text: "Não consegui buscar agora. Tenta de novo daqui a pouco?" }]);
    }
  };

  const passoDaBusca = async (resposta) => {
    const r = resposta.trim();
    if (/^(cancelar|sair|deixa|esquece|parar)$/i.test(r)) {
      setBusca(null);
      setMsgs((m) => [...m, { from: "bot", text: "Sem problema! Se mudar de ideia, é só dizer \"buscar vaga\"." }]);
      return;
    }
    const f = { ...(busca.filtros || {}) };

    if (busca.passo === "oque") {
      f.q = r;
      const n = await contar(f);
      setBusca({ passo: "onde", filtros: f, resumo: r });
      setMsgs((m) => [...m, { from: "bot", ...perguntaOnde(r, n, meuPerfil) }]);
      return;
    }

    if (busca.passo === "onde") {
      const qualquer = /^(qualquer|tanto faz|todas|indiferente|qualquer lugar|brasil)$/i.test(r);
      if (!qualquer) f.cidade = r;
      const n = await contar(f);
      // Com poucas vagas não vale gastar mais uma pergunta: mostra logo.
      if (n !== null && n > 0 && n <= 8) { setBusca(null); await buscarEMostrar(f, busca.resumo); return; }
      if (n === 0) { setBusca(null); await buscarEMostrar(f, busca.resumo); return; }
      setBusca({ passo: "modalidade", filtros: f, resumo: busca.resumo });
      setMsgs((m) => [...m, { from: "bot", ...perguntaModalidade(n) }]);
      return;
    }

    // modalidade — o único critério preenchido em 100% do acervo (medido em 25/09/2026)
    const rr = r.toLowerCase();
    if (/remot|home ?office|de casa/.test(rr)) f.remoto = true;
    // 📄 CLT/PJ/ESTÁGIO SAEM DA PRÓPRIA FRASE (26/09/2026, pedido do Nelson: *"falta adicionar mais
    // filtros clt/pj"*). De propósito NÃO virou mais uma pergunta: o fluxo já tem duas, e a terceira faz
    // a pessoa desistir antes de ver vaga nenhuma. Quem escreve "vaga CLT em Santos" é atendido; quem
    // não menciona nada continua vendo tudo.
    if (/\bclt\b|carteira assinada|celetista/.test(rr)) f.contratacao = "CLT";
    else if (/\bpj\b|pessoa jur[íi]dica/.test(rr)) f.contratacao = "PJ";
    else if (/est[áa]gi/.test(rr)) f.contratacao = "Estagio";
    else if (/tempor[áa]ri/.test(rr)) f.contratacao = "Temporario";
    else if (/h[íi]brid/.test(rr)) f.modalidade = "Hibrido";
    else if (/presencial/.test(rr)) f.modalidade = "Presencial";
    setBusca(null);
    await buscarEMostrar(f, busca.resumo);
  };


  const enviar = async (q) => {
    const pergunta = (q ?? texto).trim();
    if (!pergunta) return;
    // chips de navegação começam com "/" → apenas navega
    // Chip que leva para uma página do portal: navega E FECHA o chat. Ficava aberto por cima do
    // conteúdo que a pessoa acabou de pedir para ver — *"ao clicar no ver todas no portal ele deve
    // redirecionar a página e minimizar automaticamente o assistente"* (Nelson, 26/09/2026).
    if (pergunta.startsWith("/")) { navigate(pergunta); setAberto(false); return; }
    setTexto("");
    const historico = fazerHistorico();
    setMsgs((m) => [...m, { from: "user", text: pergunta }]);

    // Estamos no meio da busca guiada? A resposta da pessoa é o filtro, não uma pergunta nova.
    if (busca) { await passoDaBusca(pergunta); return; }
    // Ela está PEDINDO para buscar? Entra no fluxo (e já aproveita o termo, se veio junto).
    const inicio = querBuscarVaga(pergunta);
    if (inicio) {
      if (inicio.termo) { setBusca({ passo: "onde", oque: inicio.termo }); setMsgs((m) => [...m, { from: "bot", ...perguntaOnde(inicio.termo) }]); }
      else { setBusca({ passo: "oque" }); setMsgs((m) => [...m, { from: "bot", ...perguntaOque(meuPerfil) }]); }
      return;
    }

    // Pergunta sobre uma empresa específica → IA ANCORADA nos dados reais (tom caloroso, números do site).
    // Fallback ao resumo-template (preciso) se a IA estiver off/sem crédito/no limite.
    const nomeEmpresa = extrairEmpresa(pergunta);
    if (nomeEmpresa) {
      setMsgs((m) => [...m, { from: "bot", text: "🔎 Buscando a reputação...", loading: true }]);
      // IA (tom caloroso, ancorada) só p/ logado; deslogado recebe o resumo-template (preciso e grátis).
      if (user) {
        try { const r = await iaChat(pergunta, historico); if (r?.resposta) { setMsgs((m) => [...m.filter((x) => !x.loading), { from: "bot", text: r.resposta }]); return; } } catch { /* cai no template */ }
      }
      try {
        const lista = await listarEmpresas(nomeEmpresa);
        const resp = lista?.length
          ? resumoEmpresa(lista[0])
          : { text: `Ainda não encontrei "${nomeEmpresa}" na base. Quer registrar o primeiro relato sobre ela?`, chips: [{ label: "Registrar relato", q: "abrir denúncia" }] };
        setMsgs((m) => [...m.filter((x) => !x.loading), { from: "bot", ...resp }]);
      } catch {
        setMsgs((m) => [...m.filter((x) => !x.loading), { from: "bot", text: "Tive um problema ao buscar agora. Tente de novo em instantes." }]);
      }
      return;
    }

    const resp = responder(pergunta, user);
    // Sem match nas regras + LOGADO → IA real (Claude). Deslogado fica nas regras (IA é só p/ logado).
    if (resp.generico && user) {
      setMsgs((m) => [...m, { from: "bot", text: "💭 Deixa eu pensar...", loading: true }]);
      try {
        const r = await iaChat(pergunta, historico);
        setMsgs((m) => [...m.filter((x) => !x.loading),
          r?.resposta ? { from: "bot", text: r.resposta } : { from: "bot", ...resp }]);
      } catch {
        setMsgs((m) => [...m.filter((x) => !x.loading), { from: "bot", ...resp }]);
      }
      return;
    }
    setMsgs((m) => [...m, { from: "bot", ...resp }]);
    if (resp.ir === "/denunciar") setTimeout(() => openComplaint(), 400); // abre o popup, não navega
    // Mesma regra quando é o BOT que decide levar a pessoa para uma página: o pequeno atraso existe para
    // ela ler a frase antes de a tela trocar; o chat sai de cena junto com a navegação.
    else if (resp.ir) setTimeout(() => { navigate(resp.ir); setAberto(false); }, 400);
  };

  // Depois de TODOS os hooks: sair antes quebraria a ordem das chamadas entre renders.
  if (ROTAS_SEM_CHAT.some((r) => pathname.startsWith(r))) return null;

  return (
    <>
      {/* Painel do chat */}
      {aberto && (
        <div className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm rounded-2xl bg-white shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
             style={{ height: "70vh", maxHeight: 560 }}>
          <header className="bg-brand text-white px-4 py-3 flex items-center gap-3">
            <Mascote size={38} />
            <div className="flex-1">
              <div className="font-bold leading-tight">Eaí — assistente do eaiRH</div>
              <div className="text-xs text-white/80">online · respostas na hora</div>
            </div>
            <button onClick={() => setAberto(false)} aria-label="Fechar chat" className="text-white/80 hover:text-white text-xl leading-none">×</button>
          </header>

          {/* role="log" + aria-live: o leitor de tela ANUNCIA cada nova mensagem do bot/IA conforme chega
              (WCAG 4.1.3). Sem isto, respostas dinâmicas passam despercebidas por quem usa leitor de tela. */}
          {/* 🔁 RETOMAR OU COMEÇAR DE NOVO — uma vez por acesso/login, nunca a cada abertura do chat.
              A tela fica limpa atrás desta escolha: é o "limpar e perguntar" que o Nelson pediu. */}
          {perguntar && (
            <div className="border-b bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-900">
                Você tem uma conversa guardada de antes. Quer continuar de onde parou?
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => { marcarDecidido(user.id); setMsgs(lerConversa(storeKey)); setPerguntar(false); }}
                  className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white">
                  Retomar conversa
                </button>
                <button
                  onClick={() => {
                    marcarDecidido(user.id);
                    try { localStorage.removeItem(storeKey); } catch { /* privado */ }
                    setMsgs([]); setBusca(null); setPerguntar(false);
                  }}
                  className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-bold text-amber-900">
                  Começar uma nova
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50" role="log" aria-live="polite" aria-label="Conversa com o assistente Eaí">
            {msgs.map((m, i) => (
              <div key={i}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.from === "bot"
                  ? "bg-white border border-gray-200 text-gray-700"
                  : "bg-brand text-white ml-auto"}`}>
                  <TextoDoBot texto={m.text} />
                </div>
                {m.chips?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.chips.map((c) => (
                      <button key={c.q} onClick={() => enviar(c.q)}
                        className="text-xs rounded-full border border-primary text-primary px-2.5 py-1 hover:bg-primary hover:text-white transition">
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={fimRef} />
          </div>

          <div className="p-2 border-t border-gray-200 flex gap-2">
            <input value={texto} onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") enviar(); }}
              placeholder="Escreva sua dúvida..."
              aria-label="Mensagem para o assistente"
              className="flex-1 rounded-full border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <button onClick={() => enviar()} aria-label="Enviar mensagem" className="rounded-full bg-orange-600 hover:bg-orange-700 text-white w-10 h-10 flex items-center justify-center">➤</button>
          </div>
        </div>
      )}

      {/* Balão de saudação */}
      {/* No celular o balão de 240px cobria o conteúdo bem na hora de decidir — some abaixo de 640px.
          O mascote flutuante continua ali como convite. */}
      {!aberto && balao && (
        <div className="hidden sm:block fixed bottom-24 right-5 z-50 max-w-[240px]">
          <div className="relative bg-brand text-white rounded-2xl rounded-br-sm px-4 py-3 shadow-lg text-sm font-semibold">
            <button onClick={() => setBalao(false)} aria-label="Fechar saudação" className="absolute -top-2 -right-2 bg-white text-gray-500 rounded-full w-5 h-5 text-xs shadow">×</button>
            {textoBalao}
          </div>
        </div>
      )}

      {/* Botão/mascote flutuante */}
      <button onClick={() => { setAberto((v) => !v); setBalao(false); }}
        className="fixed bottom-5 right-5 z-50 rounded-full shadow-xl hover:scale-105 transition" aria-label="Abrir chat">
        <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
        <span className="relative block"><Mascote size={60} ring /></span>
      </button>
    </>
  );
}

/** Mascote "Eaí": balão de fala (mascote-1.svg) = a marca "e aí?". Fundo branco p/ contraste. */
function Mascote({ size = 56, ring }) {
  return (
    <img
      src="/mascote-1.svg" alt="Eaí" width={size} height={size}
      className="rounded-full bg-white object-contain"
      style={{ padding: size * 0.06, boxShadow: ring ? "0 0 0 3px rgba(255,255,255,.85)" : "none" }}
    />
  );
}

// ---------------- Consulta de reputação (usa a API do eaiRH) ----------------

/** Extrai o nome da empresa de perguntas tipo "referências da empresa X" / "reputação da X". */
function extrairEmpresa(q) {
  // \S* em vez de \w+ para não quebrar em acentos (reputação, avaliação, referências...)
  const m = q.match(/(?:refer[êe]ncia\S*|reputa\S*|avalia\S*|como (?:é|esta|está|anda)|me fal\S* (?:da|do|sobre)|fala (?:da|do|sobre)|info\S* (?:da|do|sobre)|sobre a|sobre o)\s+(?:empresa\s+)?(.+)/i);
  if (!m) return null;
  // tira pontuação final e artigos/preposições no início ("da", "do", "de", "a", "o")
  const nome = m[1].replace(/[?!.]+$/g, "").replace(/^(empresa|d[aeo]s?|[ao])\s+/i, "").trim();
  // evita falso-positivo quando a frase é genérica ("como funciona a reputação")
  return nome.length >= 2 && !/^(reputa\S*|funciona\S*)$/i.test(nome) ? nome : null;
}

function resumoEmpresa(e) {
  const alertas = e.alertas?.length ? ` Selos: ${e.alertas.join(", ")}.` : "";
  return {
    text: `${e.nome} — índice eaiRH ${Number(e.score).toFixed(1)} (${e.classificacao}). ` +
      // Sem relato que peça resposta, "0% deram retorno" seria acusação sem base (ex.: empresa só com elogio).
      `${e.totalDenuncias > 0 ? `${Math.round(e.indiceResposta)}% deram retorno` : "nenhum relato aguardando resposta"}${e.selo === "none" ? "" : `, nota média ${Number(e.notaMedia).toFixed(1)}`}, ` +
      `${e.totalDenuncias} reclamação(ões)${e.totalElogios > 0 ? ` e ${e.totalElogios} elogio(s)` : ""}.${alertas}`,
    chips: [{ label: "Abrir página da empresa", q: `/empresas/${e.id}` }],
  };
}

// ---------------- Cérebro local (regras) ----------------

function boasVindas(user) {
  if (user?.tipo === 1) {
    return {
      bemvindo: true,
      text: "Oi! Sou o Eaí. Posso te ajudar a responder relatos, entender sua reputação e reivindicar a página da sua empresa. Por onde começamos?",
      chips: [
        { label: "Como respondo um relato?", q: "como responder denúncia" },
        { label: "Como melhora minha reputação?", q: "como melhorar reputação" },
        { label: "Reivindicar minha empresa", q: "reivindicar empresa" },
      ],
    };
  }
  return {
    bemvindo: true,
    text: "Oi! Sou o Eaí 👋 Bem-vindo(a) ao eaiRH — aqui você confere a reputação das empresas nos processos seletivos: quem dá retorno e quem some com os candidatos. Posso te mostrar tudo, e criar sua conta é grátis. Por onde quer começar?",
    chips: [
      // A busca de vaga vem PRIMEIRO: quem chega ao portal esta procurando emprego, nao querendo
      // entender o produto. Oferecer reputacao antes de vaga e responder uma pergunta que ninguem fez.
      { label: "🔎 Buscar vagas pra mim", q: "buscar vaga" },
      { label: "Ver empresas avaliadas", q: "/empresas" },
      { label: "Como funciona?", q: "como funciona reputação" },
      { label: "Registrar um relato", q: "como registrar denúncia" },
    ],
  };
}

/**
 * Texto do balao: quebra de linha e **negrito** (20/09/2026).
 *
 * Ate aqui o balao renderizava `{m.text}` cru — entao toda quebra de linha era colapsada pelo HTML e um
 * `**assim**` aparecia com os asteriscos na tela. Passou despercebido porque as respostas antigas eram de
 * uma linha so; a lista de vagas, que tem uma vaga por linha, virava um paragrafo unico ilegivel.
 *
 * Sem dangerouslySetInnerHTML de proposito: parte deste texto vem da IA e parte vem de dado da base
 * (titulo de vaga, nome de empresa). Injetar HTML a partir de qualquer uma dessas fontes e convite a XSS.
 * Aqui o React monta os elementos, entao nao existe HTML para injetar.
 */
function TextoDoBot({ texto }) {
  if (!texto) return null;
  return String(texto).split("\n").map((linha, i) => (
    <span key={i} className="block">
      {/* O ITALICO TAMBEM (24/09/2026): a pergunta "o que voce faz?" listava os exemplos em *italico* e
          os asteriscos apareciam CRUS na tela do visitante. O negrito ja era tratado; o italico nao, e a
          IA usa os dois. A alternancia pega `**forte**` ANTES de `*enfase*`, senao o primeiro par de
          asteriscos do negrito casaria como italico vazio. */}
      {linha.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((p, j) => {
        if (p.startsWith("**") && p.endsWith("**") && p.length > 4)
          return <strong key={j}>{p.slice(2, -2)}</strong>;
        if (p.startsWith("*") && p.endsWith("*") && p.length > 2)
          return <em key={j}>{p.slice(1, -1)}</em>;
        return <React.Fragment key={j}>{p}</React.Fragment>;
      })}
    </span>
  ));
}

// ── BUSCA DE VAGAS GUIADA ────────────────────────────────────────────────────────────────────────
//
// A pessoa está pedindo vaga? E já disse QUAL? ("quero vaga de motorista" → termo = "motorista")
function querBuscarVaga(q) {
  const t = q.toLowerCase().trim();
  if (!/\b(vaga|vagas|emprego|empregos|trabalho|oportunidade|oportunidades)\b/.test(t)) return null;
  // Perguntas SOBRE o portal não são pedido de busca ("como me candidato a uma vaga?").
  if (/\b(como|por que|porque|quem|quando|o que é|funciona|cadastr|public|anunci)\b/.test(t)) return null;

  // "vaga de X em Y" / "vagas para X" → o que vem depois de de/para é o cargo
  const m = t.match(/\b(?:vagas?|empregos?|oportunidades?|trabalho)\s+(?:de|para|como|em)\s+(.{2,40}?)(?:\s+(?:em|no|na|perto|proxim|próxim)\b.*)?$/);
  const termo = m ? m[1].trim().replace(/[?!.]+$/, "") : null;
  // "vaga" sozinho não é termo de busca — aí a gente pergunta.
  return { termo: termo && termo.length >= 3 && !/^vagas?$/.test(termo) ? termo : null };
}

/**
 * Com currículo preenchido, a primeira sugestão é o que a PESSOA já disse que faz — em vez de a lista
 * genérica de sempre. Ela continua podendo escrever outra coisa; o chip só evita repetir a pergunta a
 * quem já respondeu no cadastro.
 */
function perguntaOque(perfil) {
  const area = perfil?.areaAtuacao;
  const headline = perfil?.headline;
  const chips = [];
  if (area) chips.push({ label: `${area}`, q: area });
  if (headline && headline.length <= 40 && headline !== area) chips.push({ label: headline, q: headline });

  const genericos = [
    { label: "Administrativo", q: "auxiliar administrativo" },
    { label: "Vendas", q: "vendedor" },
    { label: "Logística", q: "logística" },
    { label: "Tecnologia", q: "desenvolvedor" },
  ];
  // Os genéricos completam até 4 chips, sem repetir o que já entrou pelo perfil.
  for (const g of genericos) {
    if (chips.length >= 4) break;
    if (!chips.some((c) => c.q.toLowerCase() === g.q.toLowerCase())) chips.push(g);
  }

  return {
    text: area
      ? `Posso procurar no mural pra você! 🔎

Vi no seu currículo que você é de **${area}**. Busco por aí, ou prefere outra coisa?`
      : `Posso procurar no mural pra você! 🔎

**O que você faz?** Pode ser o cargo, a área ou uma ferramenta — por exemplo: *auxiliar administrativo*, *motorista*, *enfermagem*, *Python*.`,
    chips,
  };
}

function perguntaOnde(oque, n, perfil) {
  const quantas = n === null ? "" : n === 0
    ? `Não achei nada de **${oque}** no mural agora. 😕 Tenta outro cargo ou área?`
    : `Achei **${n}** vaga${n === 1 ? "" : "s"} de **${oque}**. `;
  return {
    text: `${quantas}${n ? "\n\n**Onde você quer trabalhar?** Escreve a cidade, ou escolhe abaixo." : ""}`,
    // A cidade do currículo vem PRIMEIRO, pelo mesmo motivo da área: quem já preencheu não deve ter que
    // digitar de novo. As capitais continuam como atalho para quem não tem cidade no perfil.
    chips: n ? (() => {
      const minha = (perfil?.cidade || "").split("/")[0].trim();
      const lista = [{ label: "Qualquer lugar", q: "qualquer" }];
      if (minha) lista.push({ label: minha, q: minha });
      for (const c of ["São Paulo", "Rio de Janeiro", "Belo Horizonte"]) {
        if (lista.length >= 4) break;
        if (!lista.some((x) => x.q.toLowerCase() === c.toLowerCase())) lista.push({ label: c, q: c });
      }
      return lista;
    })() : [{ label: "Procurar outra coisa", q: "buscar vaga" }],
  };
}

/**
 * MODALIDADE É A PERGUNTA QUE VALE (25/09/2026).
 *
 * Medido no acervo: `modalidade` está preenchida em 100% das 16.191 vagas — Presencial 83%, Híbrido 11%,
 * Remoto 6%. É o único critério, além de cargo e cidade, que filtra sem esconder o mural.
 *
 * 🚫 Contratação (CLT/PJ) e faixa salarial ficaram DE FORA de propósito: 90% das vagas não declaram o
 * regime e NENHUMA traz salário — vaga espelhada de ATS não publica isso. Oferecer esses filtros faria a
 * pessoa escolher "CLT", ver 344 de 16 mil e concluir que o portal está vazio. Eles entram quando a
 * empresa publicar direto no eaiRH, onde o formulário pergunta.
 */
function perguntaModalidade(n) {
  return {
    text: `São **${n}** vagas até aqui. **Como você quer trabalhar?**`,
    chips: [
      { label: "Tanto faz", q: "tanto faz" },
      { label: "Só remotas", q: "remoto" },
      { label: "Híbrido", q: "híbrido" },
      { label: "Presencial", q: "presencial" },
    ],
  };
}

/** O funil zerou: conta quanto teria SEM o último filtro e oferece o caminho de volta. */
function semResultado(filtros, resumo, semModalidade, semLocal) {
  const opcoes = [];
  if (semModalidade > 0) opcoes.push({ label: `Ver as ${semModalidade} de qualquer modalidade`, q: "__afrouxa_modalidade" });
  if (semLocal > semModalidade) opcoes.push({ label: `Ver as ${semLocal} em qualquer lugar`, q: "__afrouxa_local" });
  opcoes.push({ label: "Procurar outra coisa", q: "buscar vaga" });
  const dica = semModalidade > 0
    ? `Mas tem **${semModalidade}** se você aceitar outra modalidade${semLocal > semModalidade ? `, e **${semLocal}** em qualquer cidade` : ""}.`
    : semLocal > 0 ? `Mas tem **${semLocal}** em outras cidades.` : "O mural recebe vagas novas todo dia — vale tentar de novo amanhã.";
  return { text: `Com esses filtros não sobrou nenhuma vaga de **${resumo}**. 😕\n\n${dica}`, chips: opcoes };
}

/**
 * O RESULTADO MOSTRA A REPUTAÇÃO DA EMPRESA (25/09/2026).
 *
 * A lista antes dava título, empresa e cidade — o que qualquer agregador dá. A informação que só o eaiRH
 * tem é como aquela empresa trata quem se candidata, e a API de vagas já devolve isso em cada item
 * (`empresaScore`, `empresaClassificacao`). Mostrar ali é pôr o diferencial no momento em que ele decide
 * alguma coisa: antes do clique, não depois.
 */
function resultadoVagas(d, resumo, filtros) {
  const itens = d?.itens || [];
  const total = d?.total ?? itens.length;
  const onde = filtros?.remoto ? " remotas" : filtros?.cidade ? ` em ${filtros.cidade}` : "";
  const linhas = itens.map((v) => {
    const local = v.remoto ? "Remota" : (v.cidade || "—");
    const nota = v.empresaScore > 0 ? ` · nota ${String(v.empresaScore).replace(".", ",")}/10` : "";
    return `▪️ **${v.titulo}**\n   ${v.empresa} · ${local}${nota}`;
  }).join("\n\n");
  const sobra = total > itens.length ? `\n\nTem mais ${total - itens.length} no mural.` : "";
  return {
    text: `Achei **${total}** vaga${total === 1 ? "" : "s"} de **${resumo}**${onde}. As mais recentes:\n\n${linhas}${sobra}`,
    chips: [
      // 🚨 O CHIP LEVAVA SÓ O `q` (corrigido em 26/09/2026). A pessoa respondia cidade e modalidade no
      // chat, clicava em "Ver todas no mural" e caía numa lista sem esses filtros — *"a página de vagas
      // não abriu filtrada com o que solicitei a ele"*. Tudo o que ela respondeu agora vai junto.
      { label: "Ver todas no mural", q: `/vagas${paramsDoMural(filtros, resumo)}` },
      { label: "Procurar outra coisa", q: "buscar vaga" },
    ],
  };
}

/**
 * Monta a query do mural a partir do que a pessoa respondeu no chat. Os nomes dos parâmetros são os que
 * a VagasPage lê da URL (`q`, `cidade`, `remoto`, `contratacao`) — inventar nome aqui faz o filtro sumir
 * em silêncio, que é exatamente o defeito que isto corrige.
 */
function paramsDoMural(filtros, resumo) {
  const p = new URLSearchParams();
  const termo = filtros?.q || resumo;
  if (termo) p.set("q", termo);
  if (filtros?.cidade) p.set("cidade", filtros.cidade);
  if (filtros?.remoto) p.set("remoto", "true");
  if (filtros?.contratacao) p.set("contratacao", filtros.contratacao);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

function responder(q, user) {
  const t = q.toLowerCase();
  const tem = (...ks) => ks.some((k) => t.includes(k));

  if (tem("oi", "olá", "ola", "bom dia", "boa tarde", "boa noite"))
    return boasVindas(user);

  if (tem("reputaç", "score", "índice", "indice", "selo eai", "nota da empresa", "como funciona reputação"))
    return {
      text: "A reputação combina: % de empresas que deram retorno, índice de solução, nota média (0–10) e % de candidatos que voltariam a se candidatar. Isso vira um score de 0 a 10 e uma classificação (de 'Não recomendada' ao 'Selo eaiRH').",
      chips: [{ label: "Ver ranking de empresas", q: "ver indicadores" }, { label: "O que são os selos 👻🤖?", q: "o que são os selos de retorno" }],
    };

  if (tem("selo", "👻", "🤖", "automática", "automatica", "sem retorno", "não dá retorno", "nao da retorno"))
    return {
      text: "Os selos comportamentais saem dos relatos: 👻 'Costuma não dar retorno' (maioria relata sumiço), 🤖 'Usa respostas automáticas' (e-mail genérico) e 💬 'Dá retorno personalizado'. Eles aparecem na página da empresa.",
      chips: [{ label: "Ver indicadores", q: "ver indicadores" }],
    };

  if (tem("indicador", "ranking", "ver indicadores"))
    return { text: "Te levo para a página de Indicadores — rankings por taxa (solução, satisfação, resposta mais rápida e pior taxa de não-resposta).", ir: "/indicadores" };

  if (tem("denunci", "reclam", "registrar denúncia", "registrar denuncia"))
    return {
      text: "Para registrar: clique em 'Fazer relato', informe a empresa, o que houve, a etapa, o tipo de retorno (sumiço/automático/personalizado) e, se quiser, anexe prints (privados). Te levo lá?",
      chips: [{ label: "Sim, abrir formulário", q: "abrir denúncia" }],
    };
  if (tem("abrir denúncia", "abrir denuncia", "fazer denúncia", "fazer denuncia"))
    return { text: "Abrindo o formulário de relato. 📝", ir: "/denunciar" };

  if (tem("currículo", "curriculo", "perfil", "montar currículo"))
    return user?.tipo === 1
      ? { text: "O currículo é uma área do candidato — sua conta é de empresa. 🙂" }
      : user
        ? { text: "Te levo para 'Meu currículo'. Preencha área, senioridade, cidade e skills — é o que vai alimentar o match de vagas.", ir: "/curriculo" }
        : { text: "Para montar o currículo você precisa entrar na sua conta primeiro.", chips: [{ label: "Entrar", q: "fazer login" }] };
  if (tem("fazer login", "entrar", "login"))
    return { text: "Abrindo a tela de login.", ir: "/entrar" };

  if (tem("responder denúncia", "responder denuncia", "responder a denúncia", "responder o relato", "direito de resposta"))
    return { text: "Como empresa, abra a página da sua empresa, encontre o relato e clique em 'Responder'. Se o candidato contestar, você ainda tem a 'Tréplica'. Respostas rápidas e personalizadas melhoram sua reputação!", chips: [{ label: "Como melhora minha reputação?", q: "como melhorar reputação" }] };

  if (tem("melhorar reputação", "melhorar reputacao", "subir score"))
    return { text: "Responda todos os relatos (sobe o índice de resposta), resolva de fato o caso (índice de solução) e seja rápido. Evite respostas automáticas — elas geram o selo 🤖. Retorno humano gera o selo 💬." };

  if (tem("reivindicar", "minha empresa", "sou o rh"))
    return { text: "Se você tem conta de empresa, abra a página da empresa e clique em 'Sou o RH desta empresa — reivindicar'. Assim você passa a responder pelos relatos dela." };

  if (tem("vaga", "vagas", "match", "oportunidade"))
    return { text: "Temos um mural de vagas com match entre empresa e candidato! Te levo lá. Dica: mantenha seu currículo completo (área, senioridade, cidade e skills) para o match funcionar melhor.", ir: "/vagas", chips: user && user.tipo === 0 ? [{ label: "Montar currículo", q: "montar currículo" }] : [] };

  if (tem("anexo", "print", "documento", "anexar"))
    return { text: "Você pode anexar prints/PDF no relato. Eles são PRIVADOS: só a empresa dona e a moderação acessam — protege dados de terceiros (LGPD)." };

  if (tem("obrigad", "valeu", "tchau"))
    return { text: "Disponha! Qualquer coisa é só me chamar aqui no cantinho. 👋" };

  return {
    generico: true, // sinaliza "sem match" → o widget tenta a IA real (se logado e disponível)
    text: "Ainda estou aprendendo 🙂 Posso ajudar com estes temas:",
    chips: [
      { label: "Reputação", q: "como funciona reputação" },
      { label: "Relato", q: "como registrar denúncia" },
      { label: "Currículo", q: "montar currículo" },
      { label: "Indicadores", q: "ver indicadores" },
    ],
  };
}
